import { z } from 'zod'
import { scribeSourceLanguageSchema } from '@behuman/core-contract'

/**
 * Phase 3 (LD-1, LD-6, LD-19) — RHF schema rebuilt to mirror the AI tool_use
 * shape (`GenerateDraftResponseDto`) directly. Section components write into
 * these fields and the FE adapter (`draftToSoap.ts`) translates the form
 * state into the existing `SoapNoteDto` at submit time.
 *
 * Narrative fields live under `summary`; structured arrays sit as siblings of
 * `summary`. Each array entry carries its own coding system explicitly so the
 * adapter can stamp the right ICD-10 / CPT / RxNorm / LOINC URI into the
 * `SoapNoteDto.*` `CodingDto` slots.
 */

// --- Shared primitive ---
const codingSchema = z.object({
  system: z.string().optional(),
  code: z.string().optional(),
  display: z.string().optional(),
})

// --- Billing (existing modal) ---
const billingItemSchema = z.object({
  id: z.string(),
  dx: codingSchema.optional(),
})

export const billingEntrySchema = z.object({
  id: z.string(),
  procedure: codingSchema.optional(),
  price: z.number().optional(),
  quantity: z.number(),
  billingItems: z.array(billingItemSchema),
  modifiers: z.array(z.string()).default([]),
})

// --- Subjective narrative ---
// `ros` is a structured array of `{ system, finding }` rows so the FE can
// render a Review-of-Systems table that matches the mockup
// (`ui-designs/.../ChartPage.tsx:4156-4184`). The BE returns the same shape
// from the scribe `generate-draft` handler (LD-21).
const rosEntrySchema = z.object({
  system: z.string().default(''),
  finding: z.string().default(''),
})

// RHF `useFieldArray` requires object rows — flat string[] entries get
// spread into keyed objects and the typed text is lost (PD-1088).
const hpiParagraphSchema = z.object({
  id: z.string(),
  text: z.string().default(''),
})

const visitSummarySchema = z.object({
  chiefComplaint: z.string().default(''),
  // Per LD-21 — HPI is an array of paragraph rows so the FE can render the
  // mockup's per-paragraph `+ Add Paragraph` / remove controls
  // (`ui-designs/.../ChartPage.tsx:4139-4153`). The adapter joins the texts
  // with `\n\n` when writing to the legacy `SoapNoteDto.prob` slot.
  hpi: z.array(hpiParagraphSchema).default([]),
  ros: z.array(rosEntrySchema).default([]),
  assessment: z.string().default(''),
  plan: z.string().default(''),
  // Carries no "Level of service" line — that renders from the billing candidate.
  mdm: z.string().default(''),
})

export type RosEntry = z.infer<typeof rosEntrySchema>

// --- Conditions (ICD-10) ---
const conditionItemSchema = z.object({
  id: z.string(),
  code: z.string().default(''),
  display: z.string().default(''),
  category: z.enum(['problem-list-item', 'encounter-diagnosis']).default('encounter-diagnosis'),
  clinicalStatus: z
    .enum(['active', 'recurrence', 'relapse', 'inactive', 'remission', 'resolved'])
    .default('active'),
  onsetDateTime: z.string().nullable().default(null),
  severity: z.enum(['mild', 'moderate', 'severe']).nullable().default(null),
  rank: z.number().nullable().default(null),
  note: z.string().default(''),
  // Carried from the chart problem list — skipped at Lock materialization since
  // it already exists. Optional: old blobs hydrate `undefined` and are
  // backfilled from the live chart.
  external: z.boolean().optional(),
})

// --- Procedures (CPT) ---
const procedureItemSchema = z.object({
  id: z.string(),
  code: z.string().default(''),
  display: z.string().default(''),
  status: z.enum(['preparation', 'in-progress', 'completed', 'not-done']).default('completed'),
  performedDateTime: z.string().nullable().default(null),
})

// --- Medications (RxNorm) ---
const medicationItemSchema = z.object({
  id: z.string(),
  code: z.string().default(''),
  display: z.string().default(''),
  status: z.enum(['active', 'completed', 'intended', 'stopped', 'on-hold']).default('active'),
  intent: z
    .enum(['new-prescription', 'continuation', 'discontinuation'])
    .default('new-prescription'),
  dose: z.string().nullable().default(null),
  route: z.string().nullable().default(null),
  frequency: z.string().nullable().default(null),
  /** Free-text note column shown in the Plan > Medications table (mockup parity). */
  note: z.string().nullable().default(null),
  // PD-1600 — the AI could not hear or translate this row with confidence;
  // badged until the doctor confirms or edits it, and blocks Lock meanwhile.
  needsReview: z.boolean().optional(),
  reviewReason: z.string().optional(),
})

// --- Observations (LOINC) ---
const observationValueQuantitySchema = z.object({
  value: z.number(),
  unit: z.string(),
})

const observationItemSchema = z.object({
  id: z.string(),
  code: z.string().default(''),
  display: z.string().default(''),
  category: z.enum(['vital-signs', 'exam', 'laboratory', 'social-history']).default('vital-signs'),
  valueQuantity: observationValueQuantitySchema.nullable().default(null),
  valueString: z.string().nullable().default(null),
  note: z.string().nullable().default(null),
  // PD-1149 — set by the server when a vital is physiologically implausible;
  // the editor badges the row + blocks Lock until the doctor corrects/confirms.
  // PD-1600 — also set when the AI marked the value low-confidence (`reviewReason`).
  needsReview: z.boolean().optional(),
  reviewReason: z.string().optional(),
})

// --- Allergies (SNOMED/RxNorm) ---
const allergyItemSchema = z.object({
  id: z.string(),
  system: z.string().nullable().default(null),
  code: z.string().nullable().default(null),
  display: z.string().default(''),
  clinicalStatus: z.enum(['active', 'inactive', 'resolved']).default('active'),
  reaction: z.string().nullable().default(null),
  severity: z.enum(['mild', 'moderate', 'severe']).nullable().default(null),
  // Allergen class (Drug vs Non-Drug column; the chart tab renders null as
  // Non-Drug). Default null so a blank manual row never asserts a drug
  // allergy; old blobs hydrate null too.
  category: z.enum(['food', 'medication', 'environment', 'biologic']).nullable().default(null),
  // Carried from the chart (HG import or beHuman-native) — read-only row,
  // excluded from Lock materialization since it already exists. Optional: old
  // blobs hydrate `undefined` and are backfilled from the live chart.
  external: z.boolean().optional(),
  // Source chart AllergyIntolerance id, set on carry-forward rows (PD-1141) so
  // an AI-reported change can target the exact chart row. Never written back to the chart.
  chartId: z.string().nullable().default(null),
  // Chart row whose reaction/status changed during this encounter (AI-detected or
  // doctor-edited). Drives the "Updated" badge only; the row stays external and skipped at Lock.
  updated: z.boolean().optional(),
  // PD-1600 — low-confidence AI row; see `medicationItemSchema.needsReview`.
  needsReview: z.boolean().optional(),
  reviewReason: z.string().optional(),
})

// --- Family History (chart-derived, PD-1377) ---
// Rows are read-only in the note (Chart badge + remove-from-note only) and are
// never materialized at Lock — the chart's FamilyMemberHistory stays the source.
const familyHistoryItemSchema = z.object({
  id: z.string(),
  relative: z.string(),
  conditions: z.array(z.string()).default([]),
  deceased: z.boolean().optional(),
})

// --- ServiceRequests (LOINC/CPT) — Orders ---
const serviceRequestItemSchema = z.object({
  id: z.string(),
  system: z.string().default(''),
  code: z.string().default(''),
  display: z.string().default(''),
  category: z.enum(['laboratory', 'imaging', 'referral']).default('laboratory'),
  intent: z.enum(['order', 'plan', 'proposal']).default('order'),
  /** FHIR ServiceRequest.priority — shown in the Plan > Orders table (mockup parity). */
  priority: z.enum(['routine', 'urgent', 'asap', 'stat']).nullable().default('routine'),
  reasonText: z.string().nullable().default(null),
})

// --- Follow-Ups (free text) ---
// `text` is `.default('')` (not `.min(1)`) because the section now appends
// empty rows that surface an inline `<Textarea>` for the doctor to fill —
// validation can't block an empty row at append time. Empty / whitespace-only
// entries are filtered out by `draftToSoap.ts` at submit time so the BE never
// sees them.
const followUpItemSchema = z.object({
  id: z.string(),
  text: z.string().default(''),
})

// --- Encounter Details (mockup parity) ---
// LD-21 follow-up — the mockup renders Encounter Details as an editable table
// (`ui-designs/.../ChartPage.tsx:4097-4125`). The legacy `SoapNoteDto` carries
// only the primary `noteDate` + enum `visitType`; the structured table is a
// FE-only representation today. The adapter (`draftToSoap.ts`) maps the first
// row's `date` → `noteDate` so the doctor's edits round-trip; additional rows
// + the free-text `type`/`description` stay UI-only until a BE slot is added.
const encounterDetailRowSchema = z.object({
  id: z.string(),
  date: z.string().default(''),
  type: z.string().default(''),
  description: z.string().default(''),
})

export type EncounterDetailRow = z.infer<typeof encounterDetailRowSchema>

// --- Billing suggestion block (AI E/M code candidates + modifiers + POS) ---
/**
 * An AI-suggested add-on code (G2211, 99417, etc.). `reasoning` is symmetric
 * with `BillingCandidate.reasoning` — surfaced on hover in the BillingEditModal
 * via the per-entry "Suggested by AI" pill so the coder knows WHY this
 * specific add-on was attached. Empty string when unavailable (legacy DocRefs
 * pre-dating the field).
 */
const billingAddOnSchema = z.object({
  code: z.string(),
  display: z.string(),
  reasoning: z.string().default(''),
})

/**
 * One AI-proposed E/M code candidate. The AI returns 2-4 of these ranked by
 * clinical fit; each carries its own per-code `reasoning` shown on hover so
 * the coder can pick the best fit. The active selection is tracked by
 * `BillingSuggestion.selectedRank`.
 */
const billingCandidateSchema = z.object({
  code: z.string().default(''),
  display: z.string().default(''),
  rank: z.number().default(1),
  reasoning: z.string().default(''),
})

// `id` is for `useFieldArray` only — the persisted shape is label + minutes.
const timeEntrySchema = z.object({
  id: z.string(),
  label: z.string().default(''),
  minutes: z.number().default(0),
})

/** PD-2018 — an AI diagnosis's note quote and review state, keyed by code (server-stamped confirmation). */
const billingDiagnosisSupportSchema = z.object({
  code: z.string(),
  quote: z.string().default(''),
  review: z.string().default(''),
  confirmedBy: z.string().optional(),
  confirmedAt: z.string().optional(),
})

const billingSuggestionSchema = z.object({
  /** Ranked AI-proposed E/M code candidates. Empty for non-scribe / legacy notes. */
  candidates: z.array(billingCandidateSchema).default([]),
  /** Which candidate (by `rank`) is currently the active pick. Defaults to 1. */
  selectedRank: z.number().default(1),
  addOnCodes: z.array(billingAddOnSchema).default([]),
  modifiers: z.array(z.string()).default([]),
  placeOfService: z.string().default(''),
  durationMinutes: z.number().default(0),
  /** Global coder-facing caveat (NOT per-candidate). Per-code rationale lives on `candidates[].reasoning`. */
  reasoning: z.string().default(''),
  // Line items only — the face-to-face figure is `durationMinutes` above.
  providerTimeEntries: z.array(timeEntrySchema).default([]),
  careCoordinatorTimeEntries: z.array(timeEntrySchema).default([]),
  diagnoses: z.array(billingDiagnosisSupportSchema).optional(),
})

export const progressNoteEditFormSchema = z.object({
  // --- Header metadata ---
  noteDate: z.string(),
  visitType: z.enum(['new', 'follow-up']),

  // --- Encounter Details (editable table — mockup parity) ---
  encounterDetails: z.array(encounterDetailRowSchema),

  // --- Subjective narrative + structured arrays mirroring AI tool shape ---
  summary: visitSummarySchema,
  conditions: z.array(conditionItemSchema),
  procedures: z.array(procedureItemSchema),
  medications: z.array(medicationItemSchema),
  observations: z.array(observationItemSchema),
  allergies: z.array(allergyItemSchema),
  familyHistory: z.array(familyHistoryItemSchema),
  serviceRequests: z.array(serviceRequestItemSchema),
  followUps: z.array(followUpItemSchema),
  billingSuggestion: billingSuggestionSchema,

  // --- Existing Claim pipeline (separate from AI billing suggestion) ---
  billingEntries: z.array(billingEntrySchema),

  // PD-1613 — render-only: no input binds it, so the disclosure cannot be removed.
  aiAssisted: z.boolean().optional(),
  // PD-1600 — render-only like `aiAssisted`; the disclosure names the encounter language.
  encounterLanguage: scribeSourceLanguageSchema.optional(),
})

export type ProgressNoteEditFormValues = z.infer<typeof progressNoteEditFormSchema>
export type ConditionItem = z.infer<typeof conditionItemSchema>
export type ProcedureItem = z.infer<typeof procedureItemSchema>
export type MedicationItem = z.infer<typeof medicationItemSchema>
export type ObservationItem = z.infer<typeof observationItemSchema>
export type AllergyItem = z.infer<typeof allergyItemSchema>
export type FamilyHistoryItem = z.infer<typeof familyHistoryItemSchema>
export type ServiceRequestItem = z.infer<typeof serviceRequestItemSchema>
export type FollowUpItem = z.infer<typeof followUpItemSchema>
export type TimeEntryItem = z.infer<typeof timeEntrySchema>
export type BillingSuggestion = z.infer<typeof billingSuggestionSchema>
export type BillingAddOn = z.infer<typeof billingAddOnSchema>
export type BillingCandidate = z.infer<typeof billingCandidateSchema>
export type BillingDiagnosisSupport = z.infer<typeof billingDiagnosisSupportSchema>

/**
 * Return the candidate whose `rank` matches `selectedRank`. Falls back to the
 * first candidate if there's no match (defensive — e.g. the rank was hand-
 * edited or an empty array slipped through). Returns `undefined` when there
 * are no candidates at all (legacy / non-scribe-authored notes).
 */
export function getSelectedCandidate(
  suggestion: BillingSuggestion | undefined,
): BillingCandidate | undefined {
  if (!suggestion || suggestion.candidates.length === 0) return undefined
  return (
    suggestion.candidates.find((c) => c.rank === suggestion.selectedRank) ??
    suggestion.candidates[0]
  )
}

/**
 * Like {@link getSelectedCandidate} but with NO fallback to the first candidate:
 * `selectedRank: 0` means the backend could not tell new from established, and
 * the signed MDM narrative must then carry no code. Do not add a fallback here.
 */
export function getAttestedCandidate(
  suggestion: { candidates: BillingCandidate[]; selectedRank: number } | undefined,
): BillingCandidate | undefined {
  return suggestion?.candidates.find((c) => c.rank === suggestion.selectedRank)
}

/** Shared by the editor, the locked viewer and the PDF so the totals can't diverge. */
export function computeTimeTotals(
  faceToFaceMinutes: number,
  providerEntries: { minutes: number }[],
  coordinatorEntries: { minutes: number }[],
) {
  const sum = (entries: { minutes: number }[]) =>
    entries.reduce((total, entry) => total + (entry.minutes || 0), 0)
  const sameDayMinutes = sum(providerEntries)
  const coordinatorMinutes = sum(coordinatorEntries)
  const providerTotal = faceToFaceMinutes + sameDayMinutes
  return {
    sameDayMinutes,
    coordinatorMinutes,
    providerTotal,
    combinedTotal: providerTotal + coordinatorMinutes,
  }
}
export type VisitSummary = z.infer<typeof visitSummarySchema>
export type BillingEntry = z.infer<typeof billingEntrySchema>

/**
 * Empty/initial form values — used when there is no provenance to hydrate
 * from (new chart-side encounters), or as the seed for a hydrate call.
 */
export function makeEmptyProgressNoteFormValues(
  initialNoteDate: string,
  initialVisitType: 'new' | 'follow-up',
): ProgressNoteEditFormValues {
  return {
    noteDate: initialNoteDate,
    visitType: initialVisitType,
    encounterDetails: [
      {
        id: crypto.randomUUID(),
        date: initialNoteDate,
        type: initialVisitType === 'new' ? 'Initial Visit' : 'Follow-up Visit',
        description: '',
      },
    ],
    summary: {
      chiefComplaint: '',
      hpi: [],
      ros: [],
      assessment: '',
      plan: '',
      mdm: '',
    },
    conditions: [],
    procedures: [],
    medications: [],
    observations: [],
    allergies: [],
    familyHistory: [],
    serviceRequests: [],
    followUps: [],
    billingSuggestion: {
      candidates: [],
      selectedRank: 1,
      addOnCodes: [],
      modifiers: [],
      placeOfService: '',
      durationMinutes: 0,
      reasoning: '',
      providerTimeEntries: [],
      careCoordinatorTimeEntries: [],
    },
    billingEntries: [],
  }
}
